import { assertValue, DEFAULT_TRANSPORT_TYPE } from '@libs/utils';
import {
  deserializeConstraint,
  isFullTextConstraint,
  isHotelCodeConstraint,
  isPolygonIdConstraint,
} from '@libs/constraint-builder';
import { trackDeserializeStayWithoutSelectionPrefix } from '@libs/feature-toggle';
import type { SearchTerm } from '@libs/trip-planner-interfaces';
import { SearchTermType } from '@libs/trip-planner-interfaces';

import {
  CONSTRAINT_TEXT_SUFFIX,
  DISPLAY_TEXT_PREFIX,
  FLEXIBLE_NUMBER_OF_NIGHTS_SYMBOL,
} from './constants';

export interface DeserializedStayConstraintText {
  searchTerm: SearchTerm;
  displayText?: string;
}

export function deserializeStayConstraintText(
  constraintText: string
): DeserializedStayConstraintText {
  let searchTerm: SearchTerm;
  let displayText: string | undefined = undefined;

  if (constraintText.length === 0) {
    throw new Error('Invalid serialized constraint text.');
  }

  const displayTextRegExp = new RegExp(
    `(${DISPLAY_TEXT_PREFIX}(.*?)${CONSTRAINT_TEXT_SUFFIX}).*$`
  );
  const searchTextRegExp = new RegExp(
    `(?:[^${CONSTRAINT_TEXT_SUFFIX}](?!(${CONSTRAINT_TEXT_SUFFIX})))+$`
  );

  const displayTextMatch = constraintText.match(displayTextRegExp);
  const searchTextMatch = constraintText.match(searchTextRegExp);

  assertValue(
    searchTextMatch,
    'hotelSerializer.ts > deserializeConstraintText > searchTextMatch'
  );

  const matchedSearchText = searchTextMatch[0];

  const deserializedConstraintSelection =
    deserializeConstraint(matchedSearchText).selection;
  if (isPolygonIdConstraint(deserializedConstraintSelection)) {
    searchTerm = {
      type: SearchTermType.PolygonId,
      polygonId: deserializedConstraintSelection.polygonId,
    };
  } else if (
    isHotelCodeConstraint(deserializedConstraintSelection) &&
    deserializedConstraintSelection.hotelCodes[0]
  ) {
    searchTerm = {
      type: SearchTermType.HotelCode,
      hotelCode: deserializedConstraintSelection.hotelCodes[0],
    };
  } else {
    searchTerm = {
      type: SearchTermType.FreeText,
      freeText: matchedSearchText,
    };
  }

  if (isFullTextConstraint(deserializedConstraintSelection)) {
    trackDeserializeStayWithoutSelectionPrefix();
  }

  if (displayTextMatch) {
    const matchedDisplatText = displayTextMatch[2];

    assertValue(
      matchedDisplatText,
      'hotelSerializer.ts > deserializeConstraintText > matchedDisplatText'
    );

    displayText = matchedDisplatText;
  }

  return {
    searchTerm,
    displayText,
  };
}

export function deserializeNumberOfNights(
  serializedNumberOfNights: string
): number | undefined {
  if (serializedNumberOfNights !== FLEXIBLE_NUMBER_OF_NIGHTS_SYMBOL) {
    const numberOfNights = parseInt(serializedNumberOfNights, 10);
    if (!isNaN(numberOfNights)) {
      return numberOfNights;
    }
  }
}

export function deserializeTransportType(props: string): number {
  const firstValue = props[0];
  const secondValue = props[1];
  assertValue(
    firstValue,
    'hotelSerializer.ts > deserializeTransportType > firstValue'
  );
  assertValue(
    secondValue,
    'hotelSerializer.ts > deserializeTransportType > secondValue'
  );

  const result = parseInt(firstValue + secondValue, 10);

  if (isNaN(result)) {
    return DEFAULT_TRANSPORT_TYPE;
  }

  return result;
}
